/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Modal,
  notification,
  Space,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { DeleteFilled, UploadOutlined } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { formatNumber } from '@/utils/dataUtils';
import { IMediaUpload } from '@/interfaces/media';
import { uploadMedia } from '@/adapters/media';
import { isEmpty, isUndefined } from 'lodash';
import { importDoOrder } from '@/adapters/doOrder';
import { IDoOrder } from '@/interfaces/doOrder';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const ImportDoOrderModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [items, setItems] = useState<Partial<IDoOrder>[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const columns: TableProps<Partial<IDoOrder>>['columns'] = [
    {
      title: 'DO NUMBER',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'ITEM CODE',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      width: 160,
      align: 'center',
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'productName',
      key: 'productName',

      className: 'font-bold',
    },
    {
      title: 'VOLUMN',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold',
      width: 130,
      align: 'right',
      render: _ => formatNumber(_, 2),
    },
    {
      title: 'REQUEST ETD SIN',
      dataIndex: 'requestEtdSin',
      key: 'requestEtdSin',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'REQUEST ETA BKK',
      dataIndex: 'requestEtaBkk',
      key: 'requestEtaBkk',
      className: 'font-bold',
      width: 140,
      align: 'center',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      defaultSortOrder: 'descend', // or 'descend'
      sorter: (a, b) => (a.status ?? 0) - (b.status ?? 0),
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setItems(c => c.filter(x => x.code !== rec.code))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setItems([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[1]) || !isUndefined(c[1]))
        .map((c: any) => {
          const match: IDoOrder = {
            code: (c[1] || '').toString().trim(),
            productCode: (c[4] || '').toString().trim(),
            productName: (c[3] || '').toString().trim(),
            quantity: c[5],
            requestEtdSin: (c[6] || '').toString().trim(),
            requestEtaBkk: (c[7] || '').toString().trim(),
          } as IDoOrder;
          return match;
        });
      setItems(excelData);
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const upLoadmutations = useMutation({
    mutationFn: async (data: Partial<IMediaUpload>) => await uploadMedia(data),
    mutationKey: ['upload-media'],
    onSuccess: data => {
      console.log(data);
    },
    onError: error => {
      console.log(error);
    },
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IDoOrder>[]) => await importDoOrder(data),
    mutationKey: ['import-do-order'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        // setBu(0);
        notification.success({
          message: 'นำเข้า Do order สำเร็จ',
        });
        const file: Partial<IMediaUpload> = {
          file: fileList.length ? (fileList[0] as any) : null,
          modelId: 1,
          modelType: 'doorder',
        };
        upLoadmutations.mutateAsync(file);
        handleOk();
      } else {
        if (data?.data?.responseCode == 'DO002') {
          const duplicateDos: string[] = data.data?.data;
          const updateDos = items.map(c => {
            c.status = duplicateDos.includes(c.productCode as string) ? 2 : 3;
            return c;
          });
          setItems(updateDos);
        }
        if (data?.data?.responseCode == 'DO003') {
          const duplicateDos: string[] = data.data?.data;
          const updateDos = items.map(c => {
            c.status = duplicateDos.includes(c.code as string) ? 2 : 3;
            return c;
          });
          setItems(updateDos);
        }
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    const updateItems = items?.map(i => ({
      ...i,
      code: i.code?.toString().trim(),
      productCode: i.productCode?.toString().trim(),
      requestEtdSin: i.requestEtdSin
        ? dayjs(i.requestEtdSin, 'M/D/YYYY').toDate()
        : undefined,
      requestEtaBkk: i.requestEtaBkk
        ? dayjs(i.requestEtaBkk, 'M/D/YYYY').toDate()
        : i.requestEtdSin
          ? dayjs(i.requestEtdSin, 'M/D/YYYY').add(7, 'day').toDate()
          : undefined,
    }));
    mutations.mutateAsync(updateItems);
  };

  const isDisabled = !!items?.length;

  return (
    <Modal
      title="นำเข้า Stock"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          disabled={!isDisabled}
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
      afterClose={() => {
        setFileList([]);
        setItems([]);
      }}
    >
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-row gap-2 justify-between items-center">
          <Upload {...props}>
            {fileList.length === 0 && (
              <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
            )}
          </Upload>
        </div>
        <TableComponent<Partial<IDoOrder>>
          columns={columns}
          criteria={{}}
          data={{
            data: items,
            totalRecords: items.length,
          }}
          loading={false}
          onTableChange={undefined}
          scroll={false}
          rowKey="keminOrderNo"
          defaultPagination={true}
        />
      </div>
    </Modal>
  );
};

export default ImportDoOrderModal;
